import { useCallback, useEffect, useState } from "react";

const CACHE_KEY = "sb_lang_dict_cache_v1";

function readCache() {
  try {
    return JSON.parse(localStorage.getItem(CACHE_KEY) || "null");
  } catch {
    return null;
  }
}

function writeCache(cacheObj) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheObj));
  } catch {}
}

export function useLangDict() {
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "ar");

  // ✅ boot from cache immediately
  const [dict, setDict] = useState(() => {
    const cache = readCache();
    return cache?.[lang] || null;
  });

  // ✅ if we had cache, we are ready instantly
  const [ready, setReady] = useState(() => {
    const cache = readCache();
    return !!cache?.[lang];
  });

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "lang") setLang(e.newValue || "ar");
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const t = useCallback(
    (key, fallback = "") => {
      if (!dict) return ""; // prevent fallback flash
      const parts = key.split(".");
      let cur = dict;
      for (const p of parts) {
        cur = cur?.[p];
        if (cur == null) return fallback;
      }
      return typeof cur === "string" ? cur : fallback;
    },
    [dict]
  );

  useEffect(() => {
    const controller = new AbortController();

    async function loadLang() {
      // ✅ use cache immediately if exists
      const cache = readCache();
      if (cache?.[lang]) {
        setDict(cache[lang]);
        setReady(true);
      } else {
        setReady(false);
      }

      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL || "http://localhost:5000"}/api/language/${lang}`,
          { signal: controller.signal }
        );
        if (!res.ok) throw new Error("Language fetch failed");

        const data = await res.json();

        // ✅ update state
        setDict(data || {});
        setReady(true);

        // ✅ write/update cache
        const nextCache = { ...(cache || {}) };
        nextCache[lang] = data || {};
        writeCache(nextCache);
      } catch (e) {
        if (e.name !== "AbortError") console.error(e);
        // keep whatever dict we had; mark ready so UI isn't stuck
        setReady(true);
      }
    }

    loadLang();
    return () => controller.abort();
  }, [lang]);

  return { t, lang, dict, ready };
}